import React, { useEffect, useMemo, useRef, useState } from "react";
import { Routes, Route, Link, useParams } from "react-router-dom";
import api, { resolveImg } from "../lib/api";
import ProcessTimeline from "../components/ProcessTimeline";
import DashboardLayout from "../components/DashboardLayout";
import BusinessCard from "../components/BusinessCard";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { LayoutDashboard, Heart, MailQuestion, Send, Search, Info, HandCoins, CalendarDays, FileLock2, Download, ShieldCheck, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import StatusBadge from "../components/StatusBadge";
import { docTypeLabel } from "../components/BusinessDocuments";

const KIND_BADGE = {
  info:    { label: "Información",   icon: Info,        cls: "bg-slate-100 text-slate-700 border-slate-200" },
  offer:   { label: "Oferta",        icon: HandCoins,   cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  visit:   { label: "Visita",        icon: CalendarDays,cls: "bg-amber-100 text-amber-800 border-amber-200" },
  meeting: { label: "Reunión",       icon: CalendarDays,cls: "bg-violet-100 text-violet-800 border-violet-200" },
};

function KindBadge({ kind }) {
  const cfg = KIND_BADGE[kind] || KIND_BADGE.info;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg.cls}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

const fmtCLPshort = (n) => n == null ? "—" : new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n);
const fmtDateShort = (iso) => iso ? new Date(iso).toLocaleString("es-CL", { timeZone: "America/Santiago", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

const NAV = [
  { to: "/investor", icon: <LayoutDashboard className="w-4 h-4" />, label: "Panel", end: true },
  { to: "/investor/favorites", icon: <Heart className="w-4 h-4" />, label: "Mis favoritos" },
  { to: "/investor/requests", icon: <MailQuestion className="w-4 h-4" />, label: "Mis solicitudes", section: "requests" },
  { to: "/investor/documents", icon: <FileLock2 className="w-4 h-4" />, label: "Documentos", section: "documents" },
];

export default function InvestorPanel() {
  return (
    <DashboardLayout title="Inversionista" subtitle="Panel del inversionista" items={NAV} testId="investor-panel">
      <Routes>
        <Route index element={<Overview />} />
        <Route path="favorites" element={<Favorites />} />
        <Route path="requests" element={<MyRequests />} />
        <Route path="documents" element={<MyDocumentAccess />} />
        <Route path="conversations/:id" element={<Conversation />} />
      </Routes>
    </DashboardLayout>
  );
}

function Overview() {
  const [favs, setFavs] = useState([]);
  const [reqs, setReqs] = useState([]);
  useEffect(() => {
    api.get("/favorites").then((r) => setFavs(r.data)).catch(() => {});
    api.get("/requests/mine").then((r) => setReqs(r.data));
  }, []);
  const stats = [
    { label: "Favoritos guardados", value: favs.length },
    { label: "Solicitudes activas", value: reqs.filter((r) => r.status !== "closed").length },
    { label: "Solicitudes totales", value: reqs.length },
  ];
  return (
    <div className="space-y-6" data-testid="investor-overview">
      <div className="grid grid-cols-3 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="bg-white border border-zinc-200 rounded-lg p-5">
            <p className="overline text-slate-500 text-xs">{s.label}</p>
            <p className="font-display text-3xl font-extrabold mt-2">{s.value}</p>
          </div>
        ))}
      </div>
      <div className="bg-slate-900 text-white rounded-lg p-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <h3 className="font-display text-xl font-bold">Encuentra tu próxima oportunidad</h3>
          <p className="text-sm text-slate-300 mt-1">Explora el marketplace con filtros avanzados.</p>
        </div>
        <Button asChild className="btn-investment" data-testid="overview-explore">
          <Link to="/marketplace"><Search className="w-4 h-4 mr-2" /> Explorar marketplace</Link>
        </Button>
      </div>
    </div>
  );
}

function Favorites() {
  const [list, setList] = useState([]);
  const load = () => api.get("/favorites").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (b) => { await api.delete(`/favorites/${b.id}`); load(); };
  if (list.length === 0) return <p className="text-slate-500" data-testid="favorites-empty">Aún no tienes negocios favoritos.</p>;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 staggered" data-testid="favorites-grid">
      {list.map((b) => (<BusinessCard key={b.id} biz={b} onFavorite={remove} isFavorite={true} />))}
    </div>
  );
}

function MyRequests() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/requests/mine").then((r) => setList(r.data)); }, []);

  // Group all requests by business so each (investor, business) maps to ONE conversation thread.
  const groups = React.useMemo(() => {
    const map = new Map();
    list.forEach((r) => {
      const k = r.business_id;
      if (!map.has(k)) {
        map.set(k, { business_id: r.business_id, business_title: r.business_title, items: [], thread_id: r.id, latest: r.created_at });
      }
      const g = map.get(k);
      g.items.push(r);
      // earliest created_at -> thread anchor (matches backend canonical)
      if (new Date(r.created_at) < new Date(g.latest_anchor || r.created_at)) g.latest_anchor = r.created_at;
      if (!g.thread_id_anchor || new Date(r.created_at) < new Date(g.thread_id_anchor_date)) {
        g.thread_id_anchor = r.id;
        g.thread_id_anchor_date = r.created_at;
      }
    });
    return Array.from(map.values()).map((g) => ({
      ...g,
      thread_id: g.thread_id_anchor || g.items[0].id,
      // sort kinds chronologically
      items: g.items.slice().sort((a, b) => new Date(a.created_at) - new Date(b.created_at)),
      last_activity: g.items.reduce((acc, x) => new Date(x.created_at) > new Date(acc) ? x.created_at : acc, g.items[0].created_at),
    }));
  }, [list]);

  if (list.length === 0) {
    return (
      <div className="bg-white border border-zinc-200 rounded-lg p-10 text-center" data-testid="investor-requests-empty">
        <p className="text-slate-500">Aún no has solicitado información de ningún negocio.</p>
        <Button asChild className="mt-4 btn-primary"><Link to="/marketplace">Explorar marketplace</Link></Button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="investor-requests">
      <p className="text-sm text-slate-600">
        Todas tus gestiones sobre un mismo negocio se consolidan en una sola conversación con el equipo DerechoLlave. Aquí puedes ver el estado y abrir el hilo correspondiente.
      </p>
      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Negocio</TableHead>
            <TableHead>Gestiones</TableHead>
            <TableHead>Última actividad</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead>Acción</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {groups.map((g) => {
              const offerItem = g.items.find((x) => x.kind === "offer");
              const visitItem = g.items.find((x) => x.kind === "visit");
              const hasUnresolved = g.items.some((x) => ["pending", "in_review", "in_conversation"].includes(x.status));
              return (
                <TableRow key={g.business_id} data-testid={`investor-req-group-${g.business_id}`}>
                  <TableCell className="font-medium align-top py-4">
                    {g.business_title}
                    <p className="text-xs text-slate-500 mt-0.5">{g.items.length} gestión{g.items.length === 1 ? "" : "es"} · hilo único</p>
                  </TableCell>
                  <TableCell className="align-top py-4">
                    <div className="flex flex-wrap gap-1.5">
                      {g.items.map((it) => (
                        <span key={it.id} title={fmtDateShort(it.created_at)}>
                          <KindBadge kind={it.kind} />
                        </span>
                      ))}
                    </div>
                    {(offerItem || visitItem) && (
                      <div className="mt-1.5 text-xs text-slate-600 space-y-0.5">
                        {offerItem && <p>Oferta: <strong className="text-emerald-700">{fmtCLPshort(offerItem.offer_amount)}</strong></p>}
                        {visitItem && <p>Visita: <strong>{fmtDateShort(visitItem.visit_date)}</strong></p>}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 align-top py-4">{fmtDateShort(g.last_activity)}</TableCell>
                  <TableCell className="align-top py-4">
                    {hasUnresolved
                      ? <StatusBadge status="in_conversation" type="request" />
                      : <StatusBadge status={g.items[g.items.length - 1].status} type="request" />}
                  </TableCell>
                  <TableCell className="align-top py-4">
                    <Button asChild size="sm" variant="outline" data-testid={`open-conv-${g.business_id}`}>
                      <Link to={`/investor/conversations/${g.thread_id}`}>Abrir conversación</Link>
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Conversation() {
  const { id } = useParams();
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [req, setReq] = useState(null);
  const [query, setQuery] = useState("");
  const listRef = useRef(null);
  const bottomRef = useRef(null);

  const load = () => {
    api.get(`/requests/${id}`).then((r) => setReq(r.data));
    api.get(`/messages/${id}`).then((r) => setMsgs(r.data));
  };
  useEffect(() => { load(); }, [id]);

  // Auto-scroll to bottom whenever messages change (initial load + new message)
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [msgs.length]);

  const filtered = useMemo(() => {
    if (!query.trim()) return msgs;
    const q = query.toLowerCase();
    return msgs.filter((m) => (m.body || "").toLowerCase().includes(q));
  }, [msgs, query]);

  const send = async () => {
    if (!body.trim()) return;
    try {
      await api.post("/messages", { request_id: id, body });
      toast.success("Mensaje enviado");
      setBody("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="max-w-[1600px] space-y-4" data-testid="conversation-page">
      <Link to="/investor/requests" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 hover:underline" data-testid="back-to-requests">
        <ChevronLeft className="w-4 h-4" /> Volver a mis solicitudes
      </Link>
      <div className="bg-white border border-zinc-200 rounded-lg p-5 flex items-start gap-4">
        {req?.business_image && (
          <Link to={req?.business_slug ? `/business/${req.business_slug}` : "#"} className="shrink-0">
            <img
              src={resolveImg(req.business_image, 200)}
              alt={req.business_title}
              className="w-20 h-20 rounded-lg object-cover border border-zinc-200"
              data-testid="conv-business-image"
            />
          </Link>
        )}
        <div className="flex-1 min-w-0">
          <p className="overline text-emerald-700">Conversación con el equipo DerechoLlave</p>
          <h2 className="font-display text-xl font-bold truncate">{req?.business_title}</h2>
          <div className="flex items-center gap-2 mt-2"><span className="text-xs text-slate-500">Estado:</span> {req && <StatusBadge status={req.status} type="request" />}</div>
          <p className="text-xs text-slate-500 mt-2">Tu interlocutor único es el equipo de la plataforma. El vendedor recibe nuestras consultas y respuestas, pero no participa de este chat.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
        <div className="space-y-4 min-w-0">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar en la conversación…"
          className="w-full pl-9 pr-9 py-2 text-sm bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          data-testid="conv-search"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-sm"
            data-testid="conv-search-clear"
          >
            ✕
          </button>
        )}
        {query && (
          <p className="absolute -bottom-5 left-1 text-xs text-slate-500">
            {filtered.length} resultado{filtered.length === 1 ? "" : "s"}
          </p>
        )}
      </div>

      <div ref={listRef} className="rounded-lg p-5 space-y-2 max-h-[560px] overflow-y-auto border border-zinc-200" style={{ background: "#efeae2" }}>
        {filtered.length === 0 && (
          <p className="text-sm text-slate-600 text-center py-6">
            {query ? "Sin coincidencias para tu búsqueda." : "Aún no hay mensajes en el hilo."}
          </p>
        )}
        {filtered.map((m) => {
          if (m.sender_role === "system") {
            return (
              <div key={m.id} className="flex justify-center" data-testid={`sys-msg-${m.id}`}>
                <div className="text-[11px] text-slate-700 bg-white/80 border border-zinc-200 rounded-md px-3 py-1 shadow-sm">
                  {m.body}
                </div>
              </div>
            );
          }
          const mine = m.sender_role === "investor";
          const label = mine ? "Tú" : "Equipo DerechoLlave";
          const time = m.created_at ? new Date(m.created_at).toLocaleTimeString("es-CL", { timeZone: "America/Santiago", hour: "2-digit", minute: "2-digit" }) : "";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[72%] px-3 py-2 rounded-lg text-sm shadow-sm ${mine ? "rounded-tr-none" : "rounded-tl-none border border-zinc-200"}`}
                style={mine ? { background: "#d9fdd3" } : { background: "#ffffff" }}
              >
                <span className={`text-[11px] font-semibold mr-1 ${mine ? "text-emerald-700" : "text-emerald-800"}`}>{label}:</span>
                <span className="whitespace-pre-wrap break-words text-slate-900">{m.body}</span>
                {time && <span className="text-[10px] text-slate-500 ml-2 align-baseline">{time}</span>}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-4 space-y-2">
        <Textarea rows={3} placeholder="Escribe tu mensaje al equipo DerechoLlave…" value={body} onChange={(e) => setBody(e.target.value)} data-testid="conv-input" />
        <div className="flex justify-end items-center">
          <Button onClick={send} className="btn-primary" data-testid="send-msg-btn"><Send className="w-4 h-4 mr-2" /> Enviar</Button>
        </div>
      </div>
        </div>

        <aside className="lg:sticky lg:top-24 self-start">
          {req?.business_id && <ProcessTimeline businessId={req.business_id} />}
        </aside>
      </div>
    </div>
  );
}


function MyDocumentAccess() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/investor/document-access").then((r) => setList(r.data)); }, []);
  const STATUS = {
    pending:  { cls: "bg-amber-100 text-amber-800 border-amber-200", text: "Pendiente" },
    approved: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", text: "Aprobado" },
    rejected: { cls: "bg-rose-100 text-rose-800 border-rose-200", text: "Rechazado" },
    revoked:  { cls: "bg-slate-100 text-slate-700 border-slate-200", text: "Revocado" },
  };
  return (
    <div className="space-y-4" data-testid="investor-doc-access">
      <div className="flex items-center gap-2">
        <FileLock2 className="w-5 h-5 text-amber-600" />
        <h2 className="font-display text-lg font-bold">Acceso a documentos privados</h2>
      </div>
      <p className="text-sm text-slate-600">
        Aquí ves el estado de tus solicitudes de acceso a balances, contratos y otros documentos sensibles compartidos por los vendedores.
      </p>
      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Negocio</TableHead>
            <TableHead>Documentos</TableHead>
            <TableHead>Solicitada</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead>Acciones</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {list.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">Aún no has solicitado acceso a documentos.</TableCell></TableRow>}
            {list.map((r) => {
              const s = STATUS[r.status] || STATUS.pending;
              return (
                <TableRow key={r.id} data-testid={`my-doc-access-${r.id}`}>
                  <TableCell className="font-medium">{r.business_title}</TableCell>
                  <TableCell className="text-sm text-slate-600">{r.documents_count} archivo{r.documents_count === 1 ? "" : "s"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{fmtDateShort(r.created_at)}</TableCell>
                  <TableCell><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${s.cls}`}>{s.text}</span></TableCell>
                  <TableCell>
                    {r.business_slug && (
                      <Button asChild size="sm" variant="outline" data-testid={`goto-biz-${r.id}`}>
                        <Link to={`/business/${r.business_slug}`}>{r.status === "approved" ? "Ver documentos" : "Ver negocio"}</Link>
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
